import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import { CartProvider } from "@/contexts/CartContext";
import { WishlistProvider } from "@/contexts/WishlistContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import WhatsAppButton from "@/components/WhatsAppButton";
//import FloatingReviewsCarousel from "@/components/FloatingReviewsCarousel";
import CartDrawer from "@/components/CartDrawer";
import CompareDrawer from "@/components/CompareDrawer";
import HomePage from "./pages/HomePage";
import ProductsPage from "./pages/ProductsPage";
import ProductDetailPage from "./pages/ProductDetailPage";
import DealsPage from "./pages/DealsPage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import WishlistPage from "./pages/WishlistPage";
import AuthPage from "./pages/AuthPage";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminProducts from "./pages/admin/AdminProducts";
import AdminOrders from "./pages/admin/AdminOrders";
import AdminCoupons from "./pages/admin/AdminCoupons";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminHeroSettings from "./pages/admin/AdminHeroSettings";
import AdminPromoSlides from "./pages/admin/AdminPromoSlides";
import CheckoutPage from "./pages/CheckoutPage";
import MyAccountPage from "./pages/MyAccountPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import NotFound from "./pages/NotFound";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import DisclaimerPage from "./pages/DisclaimerPage";
import ScrollToTop from "./components/ScrollToTop";

const queryClient = new QueryClient();

const StorefrontLayout = ({ children }: { children: React.ReactNode }) => (
  <div className="flex flex-col min-h-screen">
    <Navbar />
    <main className="flex-1">{children}</main>
    <Footer />
    <WhatsAppButton />
   {/* <FloatingReviewsCarousel /> */}
    <CartDrawer />
    <CompareDrawer />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <HelmetProvider>
      <TooltipProvider>
        <AuthProvider>
          <CartProvider>
            <WishlistProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter future={{ v7_startTransition: true }}>
                <ScrollToTop />
                <Routes>
                  {/* Storefront routes */}
                  <Route path="/" element={<StorefrontLayout><HomePage /></StorefrontLayout>} />
                  <Route path="/products" element={<StorefrontLayout><ProductsPage /></StorefrontLayout>} />
                  <Route path="/product/:id" element={<StorefrontLayout><ProductDetailPage /></StorefrontLayout>} />
                  <Route path="/deals" element={<StorefrontLayout><DealsPage /></StorefrontLayout>} />
                  <Route path="/about" element={<StorefrontLayout><AboutPage /></StorefrontLayout>} />
                  <Route path="/contact" element={<StorefrontLayout><ContactPage /></StorefrontLayout>} />
                  <Route path="/wishlist" element={<StorefrontLayout><WishlistPage /></StorefrontLayout>} />
                  <Route path="/auth" element={<StorefrontLayout><AuthPage /></StorefrontLayout>} />
                  <Route path="/my-account" element={<StorefrontLayout><MyAccountPage /></StorefrontLayout>} />
                  <Route path="/checkout" element={<StorefrontLayout><CheckoutPage /></StorefrontLayout>} />
                  <Route path="/reset-password" element={<StorefrontLayout><ResetPasswordPage /></StorefrontLayout>} />
                  <Route path="/privacy-policy" element={<StorefrontLayout><PrivacyPolicyPage /></StorefrontLayout>} />
                  <Route path="/disclaimer" element={<StorefrontLayout><DisclaimerPage /></StorefrontLayout>} />

                  {/* Admin routes */}
                  <Route path="/admin" element={<AdminLayout />}>
                    <Route index element={<AdminDashboard />} />
                    <Route path="products" element={<AdminProducts />} />
                    <Route path="orders" element={<AdminOrders />} />
                    <Route path="coupons" element={<AdminCoupons />} />
                    <Route path="users" element={<AdminUsers />} />
                    <Route path="hero-settings" element={<AdminHeroSettings />} />
                    <Route path="promo-slides" element={<AdminPromoSlides />} />
                    <Route path="settings" element={<AdminSettings />} />
                  </Route>

                  <Route path="*" element={<NotFound />} />
                </Routes>
              </BrowserRouter>
            </WishlistProvider>
          </CartProvider>
        </AuthProvider>
      </TooltipProvider>
    </HelmetProvider>
  </QueryClientProvider>
);

export default App;
